// Generic narrative templates for fallback content
// Used when AI narratives are not available

/**
 * Generate executive summary template
 */
export function generateExecutiveSummary(location, stats) {
  const {
    totalFacilities,
    avgServiceCoverage,
    avgInfrastructure,
    criticalGaps,
  } = stats

  if (totalFacilities === 0) {
    return `No health facility data is currently available for ${location.name}.`
  }

  const coverageStatus =
    avgServiceCoverage >= 80
      ? 'strong'
      : avgServiceCoverage >= 60
        ? 'moderate'
        : 'limited'

  const infrastructureStatus =
    avgInfrastructure >= 80
      ? 'well-equipped'
      : avgInfrastructure >= 60
        ? 'adequately equipped'
        : 'under-equipped'

  return `${location.name} has ${totalFacilities} health ${totalFacilities === 1 ? 'facility' : 'facilities'} with ${coverageStatus} service coverage (${avgServiceCoverage}% average) and is ${infrastructureStatus} (${avgInfrastructure}% infrastructure readiness). ${criticalGaps > 0 ? `${criticalGaps} critical infrastructure ${criticalGaps === 1 ? 'gap' : 'gaps'} ${criticalGaps === 1 ? 'has' : 'have'} been identified.` : 'No critical infrastructure gaps identified.'}`
}

/**
 * Section templates
 */
export const sectionTemplates = {
  facilityDistribution: {
    title: 'The Healthcare Landscape',
    description: metrics =>
      `${metrics.totalFacilities} health ${metrics.totalFacilities === 1 ? 'facility' : 'facilities'} distributed across ${Object.keys(metrics.facilityDistribution).length} facility ${Object.keys(metrics.facilityDistribution).length === 1 ? 'level' : 'levels'}. The distribution shows the mix of healthcare infrastructure available in this location.`,
  },

  serviceCoverage: {
    title: 'What Services Are Available',
    description: metrics => {
      const topServices = metrics.serviceCoverage.slice(0, 3)
      const bottomServices = metrics.serviceCoverage.slice(-3)

      if (metrics.serviceCoverage.length === 0) {
        return 'Service coverage data is not available for this location.'
      }

      const topText =
        topServices.length > 0
          ? `Highest coverage: ${topServices.map(s => `${s.name} (${s.percentage}%)`).join(', ')}.`
          : ''

      const bottomText =
        bottomServices.length > 0 && bottomServices[0].percentage < 70
          ? ` Services requiring attention: ${bottomServices.map(s => `${s.name} (${s.percentage}%)`).join(', ')}.`
          : ''

      return `Analysis of service availability across all health facilities.${topText ? ' ' + topText : ''}${bottomText}`
    },
  },

  infrastructureReadiness: {
    title: 'Infrastructure & Equipment Status',
    description: metrics => {
      const gaps = metrics.infrastructureGaps

      if (gaps.length === 0) {
        return 'Infrastructure assessment shows adequate readiness across all key infrastructure categories.'
      }

      const highSeverityGaps = gaps.filter(g => g.severity === 'high')

      if (highSeverityGaps.length > 0) {
        return `Critical infrastructure gaps identified: ${highSeverityGaps.map(g => `${g.name} (${g.percentage}%)`).join(', ')}. Addressing these gaps is essential for improving service delivery.`
      }

      return `Infrastructure assessment reveals ${gaps.length} area${gaps.length === 1 ? '' : 's'} requiring improvement: ${gaps.map(g => g.name).join(', ')}.`
    },
  },

  staffing: {
    title: 'Healthcare Workforce & Human Resources',
    description: metrics => {
      const totalStaff = metrics.staffingDistribution.distribution.reduce(
        (sum, s) => sum + s.value,
        0
      )
      const categories = metrics.staffingDistribution.distribution.length

      if (totalStaff === 0) {
        return 'Staffing data is not available for health facilities in this location.'
      }

      const topStaff = metrics.staffingDistribution.distribution
        .sort((a, b) => b.value - a.value)
        .slice(0, 3)

      return `Total healthcare workforce: ${totalStaff} staff across ${categories} ${categories === 1 ? 'category' : 'categories'}. Largest groups: ${topStaff.map(s => `${s.name} (${s.value})`).join(', ')}.`
    },
  },

  locationRanking: {
    title: type => {
      const typeMap = {
        subcounties: 'Subcounty',
        parishes: 'Parish',
        villages: 'Village',
      }
      return `Performance Across ${typeMap[type] || 'Location'}s`
    },
    description: comparisons => {
      if (
        !comparisons ||
        !comparisons.rankings ||
        comparisons.rankings.length === 0
      ) {
        return 'Sub-location comparison data is not available.'
      }

      const topLocation = comparisons.rankings[0]
      const bottomLocation =
        comparisons.rankings[comparisons.rankings.length - 1]
      const scoreDiff =
        topLocation.compositeScore - bottomLocation.compositeScore

      return `Comparative analysis of ${comparisons.rankings.length} ${comparisons.type}. Top-ranked: ${topLocation.name} (score: ${topLocation.compositeScore}). Performance gap: ${scoreDiff} points between highest and lowest ranked locations.`
    },
  },

  serviceHeatmap: {
    title: 'Service Availability at a Glance',
    description: comparisons => {
      if (!comparisons || !comparisons.heatmap) {
        return 'Service coverage comparison data is not available.'
      }

      const { locations, services } = comparisons.heatmap
      return `Visual comparison of ${services.length} key services across ${locations.length} locations. Color coding indicates coverage levels: green (≥80%), yellow (50-79%), red (<50%).`
    },
  },
}

/**
 * Generate key stats for overview
 */
// eslint-disable-next-line no-unused-vars
export function generateKeyStats(stats, location) {
  const keyStats = []

  if (stats.totalFacilities > 0) {
    keyStats.push({
      label: 'Health Facilities',
      value: stats.totalFacilities,
      icon: '🏥',
    })

    keyStats.push({
      label: 'Avg. Service Coverage',
      value: `${stats.avgServiceCoverage}%`,
      icon: '📊',
      status:
        stats.avgServiceCoverage >= 80
          ? 'good'
          : stats.avgServiceCoverage >= 60
            ? 'moderate'
            : 'poor',
    })

    keyStats.push({
      label: 'Infrastructure Readiness',
      value: `${stats.avgInfrastructure}%`,
      icon: '🏗️',
      status:
        stats.avgInfrastructure >= 80
          ? 'good'
          : stats.avgInfrastructure >= 60
            ? 'moderate'
            : 'poor',
    })

    if (stats.totalStaff > 0) {
      keyStats.push({
        label: 'Healthcare Staff',
        value: stats.totalStaff,
        icon: '👨‍⚕️',
      })
    }
  }

  return keyStats
}

/**
 * Generate priority alerts template
 */
export function generatePriorityAlerts(metrics) {
  const alerts = []

  // Check for critical infrastructure gaps
  const criticalGaps = metrics.infrastructureGaps.filter(
    gap => gap.severity === 'high'
  )
  if (criticalGaps.length > 0) {
    alerts.push({
      type: 'warning',
      text: `${criticalGaps.length} critical infrastructure ${criticalGaps.length === 1 ? 'gap' : 'gaps'}: ${criticalGaps.map(g => g.name).join(', ')}`,
    })
  }

  // Check for low service coverage
  const lowCoverageServices = metrics.serviceCoverage.filter(
    service => service.percentage < 50
  )
  if (lowCoverageServices.length > 0) {
    alerts.push({
      type: 'alert',
      text: `${lowCoverageServices.length} ${lowCoverageServices.length === 1 ? 'service has' : 'services have'} coverage below 50%: ${lowCoverageServices
        .slice(0, 3)
        .map(s => s.name)
        .join(', ')}`,
    })
  }

  // Check for good performance
  const highCoverageServices = metrics.serviceCoverage.filter(
    service => service.percentage >= 80
  )
  if (
    highCoverageServices.length >= metrics.serviceCoverage.length * 0.7 &&
    alerts.length === 0
  ) {
    alerts.push({
      type: 'success',
      text: `Strong service coverage across ${highCoverageServices.length} services`,
    })
  }

  return alerts
}

/**
 * Generate insight text from data patterns
 */
export function generateDataInsights(metrics, comparisons) {
  const insights = []

  // Service coverage insights
  if (metrics.serviceCoverage.length > 0) {
    const avgCoverage =
      metrics.serviceCoverage.reduce((sum, s) => sum + s.percentage, 0) /
      metrics.serviceCoverage.length

    if (avgCoverage >= 80) {
      insights.push({
        type: 'success',
        text: `Service coverage is strong, with an average of ${Math.round(avgCoverage)}% across all services.`,
      })
    } else if (avgCoverage < 50) {
      insights.push({
        type: 'gap',
        text: `Service coverage is limited, averaging ${Math.round(avgCoverage)}%. Targeted interventions needed.`,
      })
    }
  }

  // Infrastructure insights
  if (metrics.infrastructureGaps.length > 0) {
    const highSeverityGaps = metrics.infrastructureGaps.filter(
      g => g.severity === 'high'
    )
    if (highSeverityGaps.length > 0) {
      insights.push({
        type: 'gap',
        text: `Critical infrastructure gaps in ${highSeverityGaps.map(g => g.name.toLowerCase()).join(', ')} require immediate attention.`,
      })
    }
  }

  // Comparison insights
  if (comparisons && comparisons.rankings && comparisons.rankings.length > 1) {
    const topLocation = comparisons.rankings[0]
    const avgScore =
      comparisons.rankings.reduce((sum, r) => sum + r.compositeScore, 0) /
      comparisons.rankings.length

    if (topLocation.compositeScore > avgScore * 1.2) {
      insights.push({
        type: 'comparison',
        text: `${topLocation.name} leads with significantly higher performance (score: ${topLocation.compositeScore}) compared to the average.`,
      })
    }
  }

  return insights
}
